import { RefObject, useEffect } from 'react';

interface OutsideScrollProps {
  refs: RefObject<HTMLElement>[];
  is_open: boolean;
  onClose: () => void;
  threshold?: number;
}

export const useOutsideScroll = ({
  refs,
  is_open,
  onClose,
  threshold = 10,
}: OutsideScrollProps) => {
  useEffect(() => {
    if (!is_open) return;

    const positions = refs.map((ref) =>
      ref.current ? ref.current.getBoundingClientRect().top : null
    );

    const handleScroll = () => {
      const is_scrolling = refs.some((ref, index) => {
        const position = positions[index];
        if (!ref.current || position === null) return false;

        const currentPosition = ref.current.getBoundingClientRect().top;
        const diff = Math.abs(currentPosition - position);

        return diff > threshold;
      });

      if (is_scrolling) {
        onClose();
      }
    };

    const containers = new Set<Element | Document>();

    refs.forEach((ref) => {
      if (!ref.current) return;

      let element: Element | null = ref.current;
      while (element) {
        if (isScrollable(element)) {
          containers.add(element);
        }
        element = element.parentElement;
      }
    });

    containers.add(document);

    containers.forEach((container) => {
      if (container instanceof Document) {
        window.addEventListener('scroll', handleScroll, true);
      } else {
        container.addEventListener('scroll', handleScroll, true);
      }
    });

    return () => {
      containers.forEach((container) => {
        if (container instanceof Document) {
          window.removeEventListener('scroll', handleScroll, true);
        } else {
          container.removeEventListener('scroll', handleScroll, true);
        }
      });
    };
  }, [refs, is_open, onClose, threshold]);
};

const isScrollable = (element: Element): boolean => {
  const style = window.getComputedStyle(element);
  const overflow = style.overflow + style.overflowY + style.overflowX;
  return /(auto|scroll)/.test(overflow);
};
